import React, { useState, useEffect } from 'react';
import './Deposit.css';

const Deposit = ({ currency, onBack }) => {
  const [step, setStep] = useState(1);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedCoin, setSelectedCoin] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [mpesaAmount, setMpesaAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [bankAmount, setBankAmount] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [address, setAddress] = useState('');
  const [mpesaPending, setMpesaPending] = useState(false);

  useEffect(() => {
    // Always start at step 1 to show all deposit options
    // If currency is provided, it will be used when user selects the method
    setStep(1);
    setSelectedMethod(null);
    
    // Pre-select coin if currency is provided (but still show step 1 first)
    if (currency) {
      if (currency === 'BTC') setSelectedCoin('btc');
      else if (currency === 'ETH') setSelectedCoin('eth');
      else if (currency === 'USDT') setSelectedCoin('usdt');
      else if (currency === 'USDC') setSelectedCoin('usdc');
    }
  }, [currency]);

  const selectMethod = (method) => {
    setSelectedMethod(method);
    setStep(2);
    
    // If currency was provided and method is crypto, ensure coin is selected
    if (method === 'crypto' && currency) {
      if (currency === 'BTC' && !selectedCoin) setSelectedCoin('btc');
      else if (currency === 'ETH' && !selectedCoin) setSelectedCoin('eth');
      else if (currency === 'USDT' && !selectedCoin) setSelectedCoin('usdt');
      else if (currency === 'USDC' && !selectedCoin) setSelectedCoin('usdc');
    }
  };

  const onCoinChange = (e) => {
    const coin = e.target.value;
    setSelectedCoin(coin);
    setSelectedNetwork('');
    setAddress('');
  };

  const onNetworkChange = (e) => {
    setSelectedNetwork(e.target.value);
  };

  const generateAddress = () => {
    const addresses = {
      btc: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
      eth: '0x742d35Cc6634C0532925a3b844Bc9e7595f0b9e',
      usdt: '0x742d35Cc6634C0532925a3b844Bc9e7595f0b9e',
      usdc: '0x742d35Cc6634C0532925a3b844Bc9e7595f0b9e',
      bnb: 'bnb1xy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
      sol: '7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU'
    };
    setAddress(addresses[selectedCoin] || '');
    setStep(3);
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    alert('Address copied to clipboard!');
  };

  const setMpesaAmountValue = (amount) => {
    setMpesaAmount(amount.toString());
  };

  const submitMpesa = () => {
    setMpesaPending(true);
  };

  const cancelMpesa = () => {
    setMpesaPending(false);
  };

  const confirmMpesa = () => {
    setStep(3);
    alert('M-Pesa deposit confirmed!');
  };

  const formatCardNumber = (e) => {
    let value = e.target.value.replace(/\s/g, '');
    let formatted = value.match(/.{1,4}/g)?.join(' ') || value;
    setCardNumber(formatted);
  };

  const formatExpiry = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    setCardExpiry(value);
  };

  const updateCvvPreview = (e) => {
    setCardCvv(e.target.value);
  };

  const submitCard = () => {
    setStep(3);
    alert('Card payment submitted!');
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!`);
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadText = document.getElementById('uploadText');
      if (uploadText) {
        uploadText.textContent = file.name;
      }
    }
  };

  const submitBank = () => {
    setStep(3);
    alert('Bank transfer request submitted!');
  };

  const coinOptions = {
    btc: { name: 'Bitcoin', networks: ['Bitcoin', 'Lightning Network'] },
    eth: { name: 'Ethereum', networks: ['Ethereum', 'Polygon', 'Arbitrum'] },
    usdt: { name: 'Tether', networks: ['Ethereum', 'Tron', 'Polygon'] },
    usdc: { name: 'USD Coin', networks: ['Ethereum', 'Polygon', 'Solana'] },
    bnb: { name: 'BNB', networks: ['BNB Smart Chain'] },
    sol: { name: 'Solana', networks: ['Solana'] }
  };

  const minDeposits = {
    btc: '0.0001 BTC',
    eth: '0.001 ETH',
    usdt: '10 USDT',
    usdc: '10 USDC',
    bnb: '0.01 BNB',
    sol: '0.1 SOL'
  };

  return (
    <main className="deposit-page">
      {/* Step Indicator */}
      <div className="step-indicator">
        <div className="step">
          <div className={`step-circle ${step >= 1 ? 'active' : 'inactive'}`}>1</div>
          <span className={`step-label ${step >= 1 ? 'active' : 'inactive'}`}>Method</span>
        </div>
        <div className={`step-line ${step >= 2 ? 'active' : 'inactive'}`}></div>
        <div className="step">
          <div className={`step-circle ${step >= 2 ? 'active' : 'inactive'}`}>2</div>
          <span className={`step-label ${step >= 2 ? 'active' : 'inactive'}`}>Details</span>
        </div>
        <div className={`step-line ${step >= 3 ? 'active' : 'inactive'}`}></div>
        <div className="step">
          <div className={`step-circle ${step >= 3 ? 'active' : 'inactive'}`}>3</div>
          <span className={`step-label ${step >= 3 ? 'active' : 'inactive'}`}>Confirm</span>
        </div>
      </div>

      {/* Method Selection View */}
      {step === 1 && (
        <div id="methodView" className="method-selection-view">
          {/* Trust Badges */}
          <div className="trust-badges">
            <div className="trust-badge">
              <svg className="success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
              <span>Secure Payments</span>
            </div>
            <div className="trust-badge">
              <svg className="primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
              <span>Instant Processing</span>
            </div>
          </div>

          {/* Method Grid */}
          <div className="method-grid">
            {/* Crypto */}
            <button className="method-card" onClick={() => selectMethod('crypto')}>
              <span className="method-badge popular">Popular</span>
              <div className="method-content">
                <div className="method-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11.767 19.089c4.924.868 6.14-6.025 1.216-6.894m-1.216 6.894L5.86 18.047m5.908 1.042-.347 1.97m1.563-8.864c4.924.869 6.14-6.025 1.215-6.893m-1.215 6.893-3.94-.694m5.155-6.2L8.29 4.26m5.908 1.042.348-1.97M7.48 20.364l3.126-17.727"></path>
                  </svg>
                </div>
                <div className="method-info">
                  <h3 className="method-title">Cryptocurrency</h3>
                  <p className="method-description">Fast, secure deposits with BTC, ETH, USDT & more. Low fees, instant processing.</p>
                </div>
              </div>
            </button>

            {/* M-Pesa */}
            <button className="method-card" onClick={() => selectMethod('mpesa')}>
              <span className="method-badge instant">Instant</span>
              <div className="method-content">
                <div className="method-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                    <path d="M12 18h.01"></path>
                  </svg>
                </div>
                <div className="method-info">
                  <h3 className="method-title">M-Pesa</h3>
                  <p className="method-description">Instant mobile money deposits. Perfect for Kenyan users. Available 24/7.</p>
                </div>
              </div>
            </button>

            {/* Card */}
            <button className="method-card" onClick={() => selectMethod('card')}>
              <div className="method-content">
                <div className="method-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                    <line x1="2" x2="22" y1="10" y2="10"></line>
                  </svg>
                </div>
                <div className="method-info">
                  <h3 className="method-title">Credit/Debit Card</h3>
                  <p className="method-description">Secure payments with Visa, Mastercard & more. Bank-level encryption.</p>
                </div>
              </div>
            </button>

            {/* Bank */}
            <button className="method-card" onClick={() => selectMethod('bank')}>
              <div className="method-content">
                <div className="method-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" x2="21" y1="22" y2="22"></line>
                    <line x1="6" x2="6" y1="18" y2="11"></line>
                    <line x1="10" x2="10" y1="18" y2="11"></line>
                    <line x1="14" x2="14" y1="18" y2="11"></line>
                    <line x1="18" x2="18" y1="18" y2="11"></line>
                    <polygon points="12 2 20 7 4 7"></polygon>
                  </svg>
                </div>
                <div className="method-info">
                  <h3 className="method-title">Bank Transfer</h3>
                  <p className="method-description">Direct wire transfers for large amounts. Reliable and secure transactions.</p>
                </div>
              </div>
            </button>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h3>Why deposit with us?</h3>
            <ul className="info-list">
              <li><span className="info-dot"></span> Funds credited within minutes</li>
              <li><span className="info-dot"></span> No hidden fees on deposits</li>
              <li><span className="info-dot"></span> 24/7 customer support</li>
              <li><span className="info-dot"></span> Bank-grade security encryption</li>
            </ul>
          </div>
        </div>
      )}

      {/* Crypto Form */}
      {selectedMethod === 'crypto' && step >= 2 && (
        <div id="cryptoForm" className="form-container">
          <button className="btn btn-outline btn-sm" onClick={() => { setStep(1); setSelectedMethod(null); setAddress(''); }} style={{ marginBottom: '1rem' }}>
            ← Back to Methods
          </button>
          <div className="form-card">
            {!address ? (
              <div id="cryptoStep1">
                <div className="form-group">
                  <label className="form-label">Select Cryptocurrency</label>
                  <select
                    id="coinSelect"
                    className="form-select"
                    value={selectedCoin}
                    onChange={onCoinChange}
                  >
                    <option value="">Choose a coin to deposit</option>
                    <option value="btc">₿ Bitcoin (BTC)</option>
                    <option value="eth">Ξ Ethereum (ETH)</option>
                    <option value="usdt">₮ Tether (USDT)</option>
                    <option value="usdc">$ USD Coin (USDC)</option>
                    <option value="bnb">◆ BNB (BNB)</option>
                    <option value="sol">◎ Solana (SOL)</option>
                  </select>
                </div>

                {selectedCoin && (
                  <div className="form-group">
                    <label className="form-label">Select Network</label>
                    <select
                      id="networkSelect"
                      className="form-select"
                      value={selectedNetwork}
                      onChange={onNetworkChange}
                    >
                      <option value="">Choose network</option>
                      {coinOptions[selectedCoin]?.networks.map((network, idx) => (
                        <option key={idx} value={network.toLowerCase()}>{network}</option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedCoin && selectedNetwork && (
                  <button
                    id="generateBtn"
                    className="btn btn-gold btn-full btn-lg"
                    onClick={generateAddress}
                  >
                    Generate Deposit Address
                  </button>
                )}
              </div>
            ) : (
              <div id="cryptoStep2" className="address-box">
                <div className="qr-placeholder" style={{ marginBottom: '1rem' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect width="5" height="5" x="3" y="3" rx="1"></rect>
                    <rect width="5" height="5" x="16" y="3" rx="1"></rect>
                    <rect width="5" height="5" x="3" y="16" rx="1"></rect>
                    <path d="M21 16h-3a2 2 0 0 0-2 2v3"></path>
                    <path d="M21 21v.01"></path>
                    <path d="M12 7v3a2 2 0 0 1-2 2H7"></path>
                    <path d="M3 12h.01"></path>
                    <path d="M12 3h.01"></path>
                    <path d="M12 16v.01"></path>
                    <path d="M16 12h1"></path>
                    <path d="M21 12v.01"></path>
                    <path d="M12 21v-1"></path>
                  </svg>
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label className="form-label">Deposit Address</label>
                  <div className="input-with-btn">
                    <input
                      type="text"
                      id="addressInput"
                      className="form-input mono"
                      value={address}
                      readOnly
                    />
                    <button
                      className="btn btn-outline btn-icon"
                      onClick={copyAddress}
                      style={{ width: '3rem', height: '3rem' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                  <div>
                    <p className="alert-title">Important</p>
                    <p className="alert-text">
                      Only send <span id="selectedCoinName">{coinOptions[selectedCoin]?.name || 'BTC'}</span> on the selected network. Sending other assets may result in permanent loss.
                    </p>
                  </div>
                </div>

                <div className="info-row">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <span>Minimum deposit: <span id="minDeposit">{minDeposits[selectedCoin] || '0.0001 BTC'}</span></span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* M-Pesa Form */}
      {selectedMethod === 'mpesa' && step >= 2 && (
        <div id="mpesaForm" className="form-container">
          <button className="btn btn-outline btn-sm" onClick={() => { setStep(1); setSelectedMethod(null); setMpesaPending(false); }} style={{ marginBottom: '1rem' }}>
            ← Back to Methods
          </button>
          <div className="form-card">
            {!mpesaPending ? (
              <div id="mpesaMain">
                <div className="payment-header mpesa">
                  <div className="payment-icon mpesa">M</div>
                  <div>
                    <h3 className="payment-title">M-Pesa Instant Deposit</h3>
                    <p className="payment-subtitle">Fast & secure mobile money transfer</p>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <div className="input-with-icon">
                    <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <input
                      type="tel"
                      id="mpesaPhone"
                      className="form-input"
                      placeholder="0712 345 678"
                      value={mpesaPhone}
                      onChange={(e) => setMpesaPhone(e.target.value)}
                    />
                  </div>
                  <p className="form-hint">Enter your M-Pesa registered phone number</p>
                </div>

                <div className="form-group">
                  <label className="form-label">Amount (KES)</label>
                  <input
                    type="number"
                    id="mpesaAmount"
                    className="form-input"
                    placeholder="1000"
                    min="100"
                    value={mpesaAmount}
                    onChange={(e) => setMpesaAmount(e.target.value)}
                    style={{ fontSize: '1.125rem', fontWeight: '600' }}
                  />
                  <div className="form-hint form-hint-row">
                    <span>Min: KES 100</span>
                    <span>Max: KES 150,000</span>
                  </div>
                </div>

                <div className="quick-amounts">
                  <button className="btn btn-secondary btn-sm" onClick={() => setMpesaAmountValue(500)}>500</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMpesaAmountValue(1000)}>1,000</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMpesaAmountValue(5000)}>5,000</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMpesaAmountValue(10000)}>10,000</button>
                </div>

                <button
                  id="mpesaSubmitBtn"
                  className="btn btn-gold btn-full btn-lg"
                  onClick={submitMpesa}
                >
                  Deposit via M-Pesa
                </button>

                <div className="security-footer">
                  <span className="security-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                    Secure
                  </span>
                  <span className="security-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                    Instant
                  </span>
                  <span className="security-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    No Fees
                  </span>
                </div>
              </div>
            ) : (
              <div id="mpesaPending" className="pending-state">
                <div className="pending-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <h3 className="pending-title">Payment Request Sent</h3>
                <p className="pending-text">
                  Check your phone (<span id="pendingPhone">{mpesaPhone}</span>) and enter your M-Pesa PIN to complete the deposit of <strong id="pendingAmount">KES {mpesaAmount}</strong>
                </p>
                <div className="pending-timer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>This request expires in 60 seconds</span>
                </div>
                <div className="pending-buttons">
                  <button className="btn btn-outline" onClick={cancelMpesa}>Cancel</button>
                  <button className="btn btn-gold" onClick={confirmMpesa}>I've Completed Payment</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card Form */}
      {selectedMethod === 'card' && step >= 2 && (
        <div id="cardForm" className="form-container">
          <button className="btn btn-outline btn-sm" onClick={() => { setStep(1); setSelectedMethod(null); }} style={{ marginBottom: '1rem' }}>
            ← Back to Methods
          </button>
          <div className="form-card">
            <div className="card-preview">
              <div className="card-preview-top">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                  <line x1="2" x2="22" y1="10" y2="10"></line>
                </svg>
                <div className="card-brands">
                  <div className="card-brand visa"></div>
                  <div className="card-brand mc"></div>
                </div>
              </div>
              <div id="cardNumberPreview" className="card-number">
                {cardNumber || '•••• •••• •••• ••••'}
              </div>
              <div className="card-bottom">
                <span id="expiryPreview">{cardExpiry || 'MM/YY'}</span>
                <span>CVV: <span id="cvvPreview">{cardCvv ? '•'.repeat(cardCvv.length) : '---'}</span></span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Amount (USD)</label>
              <input
                type="number"
                id="cardAmount"
                className="form-input"
                placeholder="100.00"
                min="10"
                value={cardAmount}
                onChange={(e) => setCardAmount(e.target.value)}
                style={{ fontSize: '1.125rem', fontWeight: '600' }}
              />
              <div className="form-hint form-hint-row">
                <span>Min: $10</span>
                <span>Max: $10,000</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Card Number</label>
              <input
                type="text"
                id="cardNumber"
                className="form-input mono"
                placeholder="1234 5678 9012 3456"
                maxLength="19"
                onInput={formatCardNumber}
                value={cardNumber}
              />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Expiry Date</label>
                <input
                  type="text"
                  id="cardExpiry"
                  className="form-input"
                  placeholder="MM/YY"
                  maxLength="5"
                  onInput={formatExpiry}
                  value={cardExpiry}
                />
              </div>
              <div className="form-group">
                <label className="form-label">CVV</label>
                <input
                  type="password"
                  id="cardCvv"
                  className="form-input"
                  placeholder="•••"
                  maxLength="4"
                  onInput={updateCvvPreview}
                  value={cardCvv}
                />
              </div>
            </div>

            <button
              id="cardSubmitBtn"
              className="btn btn-gold btn-full btn-lg"
              onClick={submitCard}
            >
              Pay ${cardAmount || '0.00'}
            </button>

            <div className="security-footer">
              <span className="security-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Secured by 256-bit SSL encryption
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Bank Form */}
      {selectedMethod === 'bank' && step >= 2 && (
        <div id="bankForm" className="form-container">
          <button className="btn btn-outline btn-sm" onClick={() => { setStep(1); setSelectedMethod(null); }} style={{ marginBottom: '1rem' }}>
            ← Back to Methods
          </button>
          <div className="form-card">
            <div className="payment-header crypto">
              <div className="payment-icon primary">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" x2="21" y1="22" y2="22"></line>
                  <line x1="6" x2="6" y1="18" y2="11"></line>
                  <line x1="10" x2="10" y1="18" y2="11"></line>
                  <line x1="14" x2="14" y1="18" y2="11"></line>
                  <line x1="18" x2="18" y1="18" y2="11"></line>
                  <polygon points="12 2 20 7 4 7"></polygon>
                </svg>
              </div>
              <div>
                <h3 className="payment-title">Bank Transfer</h3>
                <p className="payment-subtitle">Transfer to our bank account</p>
              </div>
            </div>

            <div className="bank-details" style={{ marginBottom: '1.5rem' }}>
              <div className="bank-row">
                <div>
                  <p className="bank-label">Bank Name</p>
                  <p className="bank-value">Kenya Commercial Bank</p>
                </div>
                <button className="btn-icon" onClick={() => copyText('Kenya Commercial Bank', 'Bank Name')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                </button>
              </div>
              <div className="bank-row">
                <div>
                  <p className="bank-label">Account Name</p>
                  <p className="bank-value">Digcoin Mining Ltd</p>
                </div>
                <button className="btn-icon" onClick={() => copyText('Digcoin Mining Ltd', 'Account Name')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                </button>
              </div>
              <div className="bank-row">
                <div>
                  <p className="bank-label">Account Number</p>
                  <p className="bank-value">1234567890123</p>
                </div>
                <button className="btn-icon" onClick={() => copyText('1234567890123', 'Account Number')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                </button>
              </div>
              <div className="bank-row">
                <div>
                  <p className="bank-label">Swift Code</p>
                  <p className="bank-value">KCBLKENX</p>
                </div>
                <button className="btn-icon" onClick={() => copyText('KCBLKENX', 'Swift Code')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="alert alert-warning" style={{ marginBottom: '1.5rem' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" x2="12" y1="8" y2="12"></line>
                <line x1="12" x2="12.01" y1="16" y2="16"></line>
              </svg>
              <p className="alert-text">Use your <strong>User ID</strong> as the payment reference for faster processing.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Amount Transferred (USD)</label>
              <input
                type="number"
                id="bankAmount"
                className="form-input"
                placeholder="100.00"
                value={bankAmount}
                onChange={(e) => setBankAmount(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Transaction Reference</label>
              <input
                type="text"
                id="bankReference"
                className="form-input"
                placeholder="Enter bank transaction reference"
                value={bankReference}
                onChange={(e) => setBankReference(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Upload Proof of Payment (Optional)</label>
              <div id="uploadArea" className="upload-area">
                <input type="file" accept="image/*,.pdf" onChange={handleUpload} />
                <svg className="upload-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" x2="12" y1="3" y2="15"></line>
                </svg>
                <p id="uploadText" className="upload-text">Click to upload or drag and drop</p>
              </div>
            </div>

            <button className="btn btn-gold btn-full btn-lg" onClick={submitBank}>
              Submit Deposit Request
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default Deposit;
